import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { type SeverityType } from '@kirin/api-client/contracts/shared/severity';
import { AlertTriangle, Check, Pencil, Trash2, X } from 'lucide-react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { SeverityBadge } from '../policylist/components/SeverityBadge';

/** Transient per-row indicator shown briefly after an add/edit/delete action. */
export type PatternRowStatus = 'new' | 'edited' | 'deleted';

/** Auto-dismiss the delete confirmation if the user takes no action (ms). */
const CONFIRM_DELETE_TIMEOUT_MS = 5000;

interface PatternCardProps {
  description: string;
  severity: SeverityType;
  /** Transient status driving the chip + row highlight (New / Edited / Deleted). */
  status?: PatternRowStatus;
  onEdit: () => void;
  onDelete: () => void;
  /** Hide the edit/delete affordances (view-only or disabled modal). */
  showActions?: boolean;
  /** Support role: keep the Trash icon visible but unclickable. */
  deleteDisabled?: boolean;
  /** Render without its own border/background/rounding so it can sit as the
   *  header of a parent container (e.g. the connected Edit drawer panel). */
  bare?: boolean;
  /**
   * Why this stored rule can never match, shown as a warning chip. Display
   * only: the rule stays in the list and can still be edited or deleted.
   */
  warning?: string;
}

const NEVER_MATCHES_LABEL = 'Never matches';

const STATUS_CHIP: Record<
  PatternRowStatus,
  { label: string; className: string }
> = {
  new: {
    label: 'New',
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
  },
  edited: {
    label: 'Edited',
    className:
      'border-amber-500/35 bg-amber-500/10 text-amber-950 dark:text-amber-100',
  },
  deleted: {
    label: 'Deleted',
    className: 'border-muted-foreground/30 bg-muted/90 text-muted-foreground',
  },
};

function rowStatusClass(status?: PatternRowStatus): string {
  if (status === 'new') {
    return 'ring-1 ring-inset ring-emerald-500/45 bg-emerald-500/10 dark:ring-emerald-400/40';
  }
  if (status === 'deleted') {
    return 'border-dashed border-muted-foreground/40 bg-muted/40 opacity-90';
  }
  return 'bg-muted/30 hover:bg-muted/50';
}

export function PatternCard({
  description,
  severity,
  status,
  onEdit,
  onDelete,
  showActions = true,
  deleteDisabled = false,
  bare = false,
  warning,
}: PatternCardProps): JSX.Element {
  const [confirmArmed, setConfirmArmed] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearConfirmTimer = (): void => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
  };

  // Clear the auto-dismiss timer on unmount to avoid setting state after teardown.
  useEffect(() => clearConfirmTimer, []);

  const armConfirm = (): void => {
    clearConfirmTimer();
    setConfirmArmed(true);
    confirmTimerRef.current = setTimeout(() => {
      setConfirmArmed(false);
      confirmTimerRef.current = null;
    }, CONFIRM_DELETE_TIMEOUT_MS);
  };

  const cancelConfirm = (): void => {
    clearConfirmTimer();
    setConfirmArmed(false);
  };

  const confirmDelete = (): void => {
    clearConfirmTimer();
    setConfirmArmed(false);
    onDelete();
  };

  const chip = status ? STATUS_CHIP[status] : null;
  // The warning chip sits in the metadata area, which hides while the row is
  // hovered (the action icons take its place), so its own tooltip can't show
  // then. The description stays visible, so it carries the reason too.
  const rowTitle =
    [description, warning && `${NEVER_MATCHES_LABEL}: ${warning}`]
      .filter(Boolean)
      .join('\n') || undefined;
  const isDeleted = status === 'deleted';

  // Hover-actionable rows hide the metadata so the absolutely-positioned action
  // icons can occupy the same right-hand spot — no reserved gap, no reflow.
  // Deleting / read-only rows keep the metadata shown.
  const fadeMetaOnHover = showActions && !isDeleted;

  return (
    <div
      className={cn(
        // Uniform row height + vertically centered content so 1- and 2-line
        // descriptions render identically.
        'group relative flex min-h-16 items-center gap-3 p-3 transition-colors',
        // `bare` rows drop their own chrome so the parent container (the Edit
        // drawer panel) provides the border/background as one connected unit.
        bare
          ? 'bg-transparent'
          : cn('rounded-md border', rowStatusClass(status))
      )}
    >
      {/* Description: takes the row width, clamped to 2 lines so the row height
          stays uniform. Full text is available in the Edit drawer / title. */}
      <span
        title={rowTitle}
        className="line-clamp-2 min-w-0 flex-1 break-words text-sm font-medium"
      >
        {description || 'No description'}
      </span>

      {/* Metadata, flush right: severity badge then the transient status chip
          (rightmost). Fades out on hover so the action overlay can take its
          place; stays visible on deleting / read-only rows. */}
      <div
        className={cn(
          'flex flex-shrink-0 items-center gap-2',
          // Hide instantly (visibility, not an opacity fade) so the badge never
          // visibly overlaps the action icons; the box keeps its space, so
          // nothing reflows. Hide on hover OR keyboard focus (so the focus-
          // revealed icons don't overlap), and unconditionally while the delete
          // prompt is armed (so the metadata can't peek behind it).
          fadeMetaOnHover &&
            (confirmArmed
              ? 'invisible'
              : 'group-hover:invisible group-focus-within:invisible')
        )}
      >
        {warning && (
          <span
            title={warning}
            className="flex items-center gap-1 rounded border border-amber-500/35 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-950 dark:text-amber-100"
          >
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            {NEVER_MATCHES_LABEL}
            {/* `title` is mouse-only; screen readers get the reason as text. */}
            <span className="sr-only">: {warning}</span>
          </span>
        )}
        <SeverityBadge riskLevel={severity} showIcon />
        {chip && (
          <span
            className={cn(
              'rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none',
              chip.className
            )}
          >
            {chip.label}
          </span>
        )}
      </div>

      {/* Action overlay: absolutely positioned so it reserves no layout space.
          Hidden until hover/focus; on confirm it shows the delete prompt and its
          solid background masks the metadata underneath. */}
      {showActions && !isDeleted ? (
        confirmArmed ? (
          <div
            role="dialog"
            aria-label="Confirm delete"
            className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2 rounded-md border bg-background px-2 py-1 text-xs shadow-sm"
          >
            <span className="font-medium">Confirm delete?</span>
            <button
              type="button"
              onClick={confirmDelete}
              aria-label="Confirm delete"
              className="text-emerald-600 hover:text-emerald-700"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={cancelConfirm}
              aria-label="Cancel delete"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onEdit}
              aria-label="Edit rule"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={armConfirm}
              disabled={deleteDisabled}
              aria-label="Delete rule"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )
      ) : null}
    </div>
  );
}
